/**
 * Lazy GSAP + ScrollTrigger (+ SplitText) loader for scroll-orchestrated
 * scenes (hero → Introduction transition; Flyways Map). GSAP is only ever
 * loaded through here so it stays out of the main client bundle and plugins
 * are registered exactly once (ADR-005, docs/04-motion.md).
 */

import { getLenisInstance } from '@/lib/scroll';

import type Lenis from 'lenis';

export type ScrollSceneRuntime = {
  gsap: typeof import('gsap').gsap;
  ScrollTrigger: typeof import('gsap/ScrollTrigger').ScrollTrigger;
  SplitText: typeof import('gsap/SplitText').SplitText;
};

let runtimePromise: Promise<ScrollSceneRuntime> | null = null;

/** ScrollTrigger class currently bound for Lenis sync (set by the hero scene). */
let boundScrollTrigger: ScrollSceneRuntime['ScrollTrigger'] | null = null;
let boundLenis: Lenis | null = null;
let lenisScrollHandler: (() => void) | null = null;

export function loadScrollSceneRuntime(): Promise<ScrollSceneRuntime> {
  runtimePromise ??= Promise.all([
    import('gsap'),
    import('gsap/ScrollTrigger'),
    import('gsap/SplitText'),
  ]).then(([gsapModule, scrollTriggerModule, splitTextModule]) => {
    const { gsap } = gsapModule;
    const { ScrollTrigger } = scrollTriggerModule;
    const { SplitText } = splitTextModule;
    gsap.registerPlugin(ScrollTrigger, SplitText);
    return { gsap, ScrollTrigger, SplitText };
  });

  return runtimePromise;
}

function clearLenisScrollHandler(): void {
  if (boundLenis && lenisScrollHandler) {
    boundLenis.off('scroll', lenisScrollHandler);
  }
  boundLenis = null;
  lenisScrollHandler = null;
}

function attachLenisScrollHandler(): void {
  clearLenisScrollHandler();

  const lenis = getLenisInstance();
  if (!lenis || !boundScrollTrigger) return;

  const ScrollTrigger = boundScrollTrigger;
  const update = () => ScrollTrigger.update();
  lenis.on('scroll', update);
  boundLenis = lenis;
  lenisScrollHandler = update;
}

/**
 * Register ScrollTrigger for Lenis sync and attach if Lenis is already live.
 * Returns an unsubscribe that clears the binding (hero scene cleanup).
 */
export function bindScrollTriggerLenisSync(
  ScrollTrigger: ScrollSceneRuntime['ScrollTrigger'],
): () => void {
  boundScrollTrigger = ScrollTrigger;
  attachLenisScrollHandler();

  return () => {
    if (boundScrollTrigger !== ScrollTrigger) return;
    clearLenisScrollHandler();
    boundScrollTrigger = null;
  };
}

/**
 * Re-attach (or detach) the Lenis → ScrollTrigger scroll listener after Lenis
 * is created or destroyed. Safe to call when no ScrollTrigger is bound yet.
 */
export function rebindScrollTriggerLenisSync(): void {
  attachLenisScrollHandler();
}

/**
 * Keep ScrollTrigger in lockstep with Lenis' smoothed frames. Lenis drives
 * native `window` scroll, so ScrollTrigger works without this — the explicit
 * sync just removes any one-frame lag. Returns an unsubscribe function.
 *
 * If Lenis is not registered yet, the ScrollTrigger binding is retained so
 * {@link rebindScrollTriggerLenisSync} can attach when Lenis appears later.
 */
export function syncScrollTriggerWithLenis(
  ScrollTrigger: ScrollSceneRuntime['ScrollTrigger'],
): () => void {
  return bindScrollTriggerLenisSync(ScrollTrigger);
}

/** Push the current scroll position into scrub/pin state (post-restore). */
export function updateScrollTrigger(): void {
  boundScrollTrigger?.update();
}

/** Cheap bfcache safety — refresh pin positions if the runtime is already loaded. */
export function refreshScrollTriggerIfBound(): void {
  boundScrollTrigger?.refresh();
}
